import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, redirect } from 'react-router-dom';
import {
    graphLayoutQueryOptions,
    entityDetailsQueryOptions,
    scadaDefaultPageQueryOptions,
} from './queries/queryOptions';

export interface ScadaLoaderData {
    graphDetails: any | null;
    entityDetails: any | null;
    assetId: string;
}

export const scadaLoader =
    (queryClient: QueryClient) =>
        async ({ request, params }: LoaderFunctionArgs): Promise<ScadaLoaderData | Response> => {
            const url = new URL(request.url);
            const path = url.pathname.replace(/^\/|\/$/g, '');
            const pathSegments = path.split('/');
            const assetId = pathSegments[pathSegments.length - 1] || '';
            const workspaceId = params.workspaceId || '';

            // On the bare workspace root, send the user to their default page (if any)
            // before anything renders — no flash, URL/breadcrumbs/tracking all match.
            // `?skipAutoNav=true` lets the user explicitly view the workspace root.
            const isWorkspaceRoot = !!workspaceId && assetId === workspaceId;
            const skipAutoNav = url.searchParams.get('skipAutoNav') === 'true';
            if (isWorkspaceRoot && !skipAutoNav) {
                // A failed default lookup must never block the page — fall through and
                // render the workspace root, which is where we land by default.
                // ensureQueryData respects staleTime and shares the cache with
                // useScadaDefaultPage, so repeat root visits don't refetch.
                try {
                    const defaultLink = await queryClient.ensureQueryData(
                        scadaDefaultPageQueryOptions(workspaceId)
                    );
                    // Only redirect to a link that belongs to THIS workspace and is
                    // deeper than the bare root — guards against cross-workspace /
                    // malformed links and redirect loops.
                    if (defaultLink?.startsWith(`plant-view/${workspaceId}/`)) {
                        return redirect(`/${defaultLink}`);
                    }
                } catch {
                    // Ignore — stay on the workspace root.
                }
            }

            if (!assetId) {
                return {
                    graphDetails: null,
                    entityDetails: null,
                    assetId: '',
                };
            }
            const graphQuery = graphLayoutQueryOptions(assetId);
            const entityQuery = entityDetailsQueryOptions(assetId);

            const [graphDetails, entityDetails] = await Promise.all([
                queryClient.getQueryData(graphQuery.queryKey) ?? (await queryClient.fetchQuery(graphQuery)),
                queryClient.getQueryData(entityQuery.queryKey) ?? (await queryClient.fetchQuery(entityQuery)),
            ]);

            return {
                graphDetails,
                entityDetails,
                assetId,
            };
        };

export { graphLayoutQueryOptions, entityDetailsQueryOptions };

